import { memo, useCallback, useState, type KeyboardEvent, type CSSProperties } from 'react'
import { twMerge } from 'tailwind-merge'
import { Cell } from '@highlight/ui'
import type { CalendarEvent } from '@highlight/common'
import { CalendarEventIconView } from '@renderer/components/Icons/CalendarEventIcon'
import { useCalendars, type Calendar } from '@renderer/hooks/use-calendars'
import { useViewActions, useViewOpenHandlers, type ViewOpenTarget } from '../../view-atoms'
import { SIDEPANEL_CELL_SURFACE } from '../cells/surface'
import { useOpenBrief } from './brief/use-open-brief'
import { useMeetingBrief } from './brief/use-meeting-brief'
import { MEETING_VIEW_ID } from './meeting-view-id'
import { BriefPreview } from './brief/components/brief-preview'
import { MeetingRowContextMenuItems, MeetingRowHoverButtons } from './meeting-row-actions'
import { CellContextMenu } from '../cell-actions'
import { MeetingTimeLine } from './meeting-time-line'

interface MeetingRowProps {
  meeting: CalendarEvent
  sourceLabel?: string
  className?: string
  style?: CSSProperties
  /**
   * Hang the ready brief under the time line, tied to the row by a hairline
   * dropping from the icon. The Upcoming view turns this on — there every
   * meeting is a row, so a row is the only thing a brief can hang from (Figma
   * `7652:28726`). Home leaves it off: its lead card carries the brief instead.
   */
  showBriefPreview?: boolean
}

interface MeetingRowViewProps extends MeetingRowProps {
  calendars: Array<Calendar> | undefined
}

/**
 * The flat row Home's Upcoming uses for every meeting after the lead one
 * (Figma `7096:18818`): icon, title, and the time line, with no container.
 *
 * Deliberately not a variant of `MeetingCell`. The cell mounts the live-audio
 * feeds, the linked-notes query, and the join/transcribe machinery to decide
 * which CTA to show — a row shows none of that, and every remaining meeting
 * today renders one, so paying for those subscriptions per row would put the
 * whole section back on the live-recording re-render path that
 * `SidepanelUpcomingSection` narrows its own subscriptions to avoid.
 *
 * Everything a row needs is already inline on the calendar event.
 */
export const MeetingRowView = memo(function MeetingRowView({
  meeting,
  sourceLabel = 'Home',
  className,
  style,
  showBriefPreview = false,
  calendars,
}: MeetingRowViewProps) {
  const { openView } = useViewActions()
  const openBrief = useOpenBrief()

  // A row always drills into the meeting — the Meeting View renders an upcoming
  // meeting with nothing recorded yet perfectly well, and sending people out to
  // the calendar instead was the surprising part of clicking a meeting. The
  // calendar event stays one click away from the view's own header.
  //
  // A ready brief routes through `openBrief` so the view opens on it. Presence
  // is `brief.conversationId` on the calendar GlobalState shell; the body is
  // fetched when the row shows a preview. Everything else opens plain,
  // deliberately *not* through `openBrief` — that path treats an open as
  // consent to generate a brief, and a row click is now just navigation.
  const hasBriefMeta = !!meeting.brief?.conversationId
  const hasBrief = hasBriefMeta || !!meeting.briefGenerating
  const { data: fetchedBrief } = useMeetingBrief(meeting.id, {
    enabled: showBriefPreview && hasBriefMeta,
    pollWhilePending: hasBriefMeta,
    presenceConversationId: meeting.brief?.conversationId,
  })
  const legacyInlineBrief = meeting.brief?.brief?.trim() ? meeting.brief : null
  const inlineBrief = showBriefPreview ? (fetchedBrief ?? legacyInlineBrief) : null
  const openWith = useCallback(
    (open: ViewOpenTarget) => {
      if (hasBrief) {
        openBrief({ calendarEventId: meeting.id, title: meeting.title, meeting, sourceLabel, ...open })
        return
      }
      openView(
        MEETING_VIEW_ID,
        { mode: 'note', calendarEventId: meeting.id, title: meeting.title, meeting },
        { sourceLabel, ...open },
      )
    },
    [hasBrief, meeting, openBrief, openView, sourceLabel],
  )
  const { onOpen: open, onPopout: openInWindow } = useViewOpenHandlers(openWith)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      // The Join / View Summary cluster sits inside this role=button. Their
      // own activation already handles the key — treating it as an open would
      // fire both, and preventDefault here would also swallow Space on Join.
      if (e.target !== e.currentTarget) return
      e.preventDefault()
      open()
    },
    [open],
  )

  const hasEnded = !!meeting.end && new Date(meeting.end).getTime() <= Date.now()

  // The hover cluster's data needs (join machinery, linked-notes query) are the
  // subscriptions this row exists to avoid, so its component mounts only once
  // the row has actually been reached — pointer or keyboard focus, so tabbing
  // onto the row puts the cluster's buttons in reach too — and stays mounted,
  // since the queries are shared and the cluster hides itself via CSS when the
  // hover ends.
  const [hasHovered, setHasHovered] = useState(false)

  return (
    <CellContextMenu items={<MeetingRowContextMenuItems meeting={meeting} onPopout={openInWindow} />}>
      <div
        role="button"
        tabIndex={0}
        aria-label={meeting.title}
        onClick={open}
        onKeyDown={handleKeyDown}
        onPointerEnter={() => setHasHovered(true)}
        onFocus={() => setHasHovered(true)}
        className={twMerge(
          'group/cell group/action relative flex w-full cursor-pointer select-none flex-col gap-3 px-3 py-2',
          SIDEPANEL_CELL_SURFACE,
          className,
        )}
        style={style}
      >
        {/* Ended rows fade their content, not the row element itself: opacity on
            the cluster's host would wash out the still-live View Summary button
            and, by creating a stacking context, trap its z-index under the next
            row — the same split MeetingCell makes between its wrapper and Cell. */}
        <div className={twMerge('flex w-full flex-col gap-3', hasEnded && 'opacity-45')}>
          <div className="flex w-full items-start gap-2">
            <div className="flex shrink-0 flex-col items-center self-stretch">
              <Cell.Icon>
                <CalendarEventIconView event={meeting} size={14} calendars={calendars} />
              </Cell.Icon>
              {/* The hairline drops out of the icon and runs alongside the time line
                into the brief, so it only makes sense when a brief is there. The
                negative bottom margin carries it across the row's 12px gap so it
                lands on the brief's top edge rather than stopping short of it. */}
              {inlineBrief?.brief?.trim() && <span aria-hidden="true" className="bg-element-hover -mb-3 w-px flex-1" />}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="text-foreground type-ui-sm truncate font-normal">{meeting.title}</div>
              <MeetingTimeLine meeting={meeting} />
            </div>
          </div>
          {inlineBrief?.brief?.trim() && (
            // Full-bleed to the row's padding — flush with the icon's left edge, not
            // indented to the title's (Figma `7652:28726`), matching how Home's
            // MeetingCell hangs the same card.
            <BriefPreview
              brief={inlineBrief.brief}
              relevantLinks={inlineBrief.relevantLinks}
              showLinks={false}
              // No onClick of its own: the whole row already opens the brief, and
              // a handler here would fire and then bubble into the row's, opening
              // the view twice.
              // Same surface colour as the row it sits in (per Figma) — the
              // hairline edge and soft shadow are what separate it, not a fill.
              className="corner-smooth rounded-lg border-0 bg-transparent px-3 py-2 text-xs shadow-md"
            />
          )}
        </div>
        {/* Not gated on hasEnded: an ended meeting can't be joined, but its
            summary (if one was saved) is exactly what a past row still offers —
            the cluster renders nothing when neither applies. */}
        {hasHovered && <MeetingRowHoverButtons meeting={meeting} onPopout={openInWindow} />}
      </div>
    </CellContextMenu>
  )
})

export const MeetingRow = memo(function MeetingRow(props: MeetingRowProps) {
  const { calendars } = useCalendars()
  return <MeetingRowView {...props} calendars={calendars} />
})
